import React from 'react'
import { Alert } from '@mantine/core'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import isFuture from 'date-fns/isFuture'
import parseISO from 'date-fns/parseISO'

import { DetailsCard } from './DetailsCard'
import { DocumentsCard } from './DocumentsCard'
import {
  CancelBookingContactCard,
  ContactInfoAlert,
  EventLinksCard,
  GetReadyCard,
  InProgressAlert,
  RemindersAlert,
  SupportAlert,
} from './InfoCards'
import { RecordingsCard } from './RecordingsCard'
import { CourtReporterPageEvent } from './types'

import { CourtReporterSource } from '@/apiClient'
import { AttendeesCard } from '@/components/Event/Detail/components/AttendeesCard'
import { Layout } from '@/components/Event/Detail/components/Layout'
import { JoinCallButton } from '@/components/Event/Detail/components/LinksCard'

type Props = { event: CourtReporterPageEvent; isAdmin?: boolean }

export const EventDetail: React.FC<Props> = ({ event, isAdmin }) => {
  if (event.court_reporter_status === 'cancelled') {
    return <CancelledEventDetail event={event} />
  }

  if (event.court_reporter_status === 'past') {
    return <PastEventDetail event={event} isAdmin={isAdmin} />
  }

  if (event.status === 'ongoing') {
    return <OngoingEventDetail event={event} isAdmin={isAdmin} />
  }

  return <BookedEventDetail event={event} isAdmin={isAdmin} />
}

const BookedEventDetail: React.FC<Props> = ({ event, isAdmin }) => (
  <Layout
    left={
      isAdmin ? (
        <Alert title="Upcoming event">
          {startingTitle(parseISO(event.datetime))}.
        </Alert>
      ) : (
        <>
          <Alert title={startingTitle(parseISO(event.datetime))}>
            Prepare for your event.
          </Alert>
          <RemindersAlert />
          <GetReadyCard event={event} />
          <ContactInfoAlert />
          <CancelBookingContactCard />
        </>
      )
    }
    right={
      <>
        <DetailsCard event={event} />
        <EventLinksCard event={event} />
        <AttendeesCard event={event} />
      </>
    }
  />
)

const OngoingEventDetail: React.FC<Props> = ({ event, isAdmin }) => (
  <Layout
    left={
      isAdmin ? (
        <Alert title="Event is in progress">
          <JoinCallButton event={event} asHost={false} />
        </Alert>
      ) : (
        <>
          <InProgressAlert event={event} />
          <RemindersAlert />
          <ContactInfoAlert />
        </>
      )
    }
    right={
      <>
        <DetailsCard event={event} />
        {!isAdmin && <SupportAlert />}
        <AttendeesCard event={event} />
      </>
    }
  />
)

const PastEventDetail: React.FC<Props> = ({ event, isAdmin }) => (
  <Layout
    left={
      <>
        {isAdmin ? (
          <Alert title="Past event" color="gray">
            This event has ended.
          </Alert>
        ) : (
          <>
            <Alert title="Past event" color="gray">
              Invoice and transcript uploads are required within the order time
              indicated in your deposition. This page will always be available
              for reference or to add documents.
            </Alert>
            <ContactInfoAlert />
          </>
        )}
        {takesDeliverables(event) && (
          <DocumentsCard event={event} isAdmin={isAdmin} />
        )}
        <RecordingsCard event={event} />
      </>
    }
    right={
      <>
        <DetailsCard event={event} />
        <AttendeesCard event={event} />
      </>
    }
  />
)

const CancelledEventDetail: React.FC<Props> = ({ event }) => (
  <Layout
    left={
      <Alert title="Canceled event" color="gray">
        This event was cancelled.
      </Alert>
    }
    right={
      <>
        <DetailsCard event={event} />
        <AttendeesCard event={event} />
      </>
    }
  />
)

// Parrot keeps deliverables only on the events it staffs with a CR.
const takesDeliverables = (event: CourtReporterPageEvent) =>
  event.court_reporter_source === CourtReporterSource.filevine ||
  event.court_reporter_source === CourtReporterSource.requested

const startingTitle = (start: Date) =>
  isFuture(start)
    ? `Event is starting in ${formatDistanceToNow(start)}`
    : 'Event is starting'
